import type { IdentifyRequirement, VerifiedPrefillContext } from '@onefootprint/request-types';
import type { BootstrapData, DIMetadata } from '@/idv/types';
import { getLogger } from '@/idv/utils';
import validatePhoneAndEmail from './validate-bootstrap-data';

const { logError } = getLogger({ location: 'identify-reducer' });

export type State = {
  identifyToken: string;
  requirement?: IdentifyRequirement;
  /** The stack of previous requirements that can be re-visited using the back button. */
  requirementHistory: IdentifyRequirement[];
  email?: DIMetadata<string>;
  phoneNumber?: DIMetadata<string>;
  bootstrapData?: BootstrapData;
  /** True if a challenge has already been completed. */
  disableReset: boolean;
  /** True if a passkey is registered for the logged-in user. */
  isPasskeyRegistered?: boolean;
  /** Context on whether this playbook will try to prefill the user's info using Verified */
  verifiedPrefillContext?: VerifiedPrefillContext;
  /** True if we still need to display terms and conditions to the user */
  displayTermsAndConditions: boolean;
};

export type NextAction = {
  type: 'next';
  requirement: IdentifyRequirement;
  verifiedPrefillContext?: VerifiedPrefillContext;
  displayTermsAndConditions: boolean;
  identifyToken?: string;
  email?: string;
  phoneNumber?: string;
};

export type Action = NextAction | { type: 'prev' } | { type: 'resetToLoginWithDifferentAccount' };

export const getInitialState = (bootstrapData?: BootstrapData): State => {
  const { email, phoneNumber } = validatePhoneAndEmail(bootstrapData);
  return {
    identifyToken: '',
    requirement: undefined,
    requirementHistory: [],
    email,
    phoneNumber,
    bootstrapData,
    disableReset: false,
    verifiedPrefillContext: undefined,
    displayTermsAndConditions: true,
  };
};

const computeNextVaultData = (current?: DIMetadata<string>, value?: string) => {
  // Don't support clearing the value
  if (!value) {
    return current;
  }
  // If the value changes, clear the `isBootstrap` flag
  const isBootstrap = current?.value === value && current.isBootstrap;
  return { value, isBootstrap };
};

export const wasPasskeyRegistered = (state: State) => {
  const isPasskeyRegisteredCurrentReq =
    state.requirement?.kind === 'login' && state.requirement?.user.availableChallengeKinds.includes('biometric');
  return state.isPasskeyRegistered || isPasskeyRegisteredCurrentReq;
};

/** Unique key for a requirement that can be used to determine if we receive duplicate requirements */
const requirementKey = (requirement?: IdentifyRequirement) => {
  if (!requirement) {
    return undefined;
  }
  if (requirement.kind === 'challenge') {
    return `${requirement.kind}-${requirement.authMethod}`;
  }
  if (requirement.kind === 'collect_data') {
    return `${requirement.kind}-${requirement.cdo}`;
  }
  return requirement.kind;
};

export const reducer = (state: State, action: Action): State => {
  switch (action.type) {
    case 'next': {
      const identifyToken = action.identifyToken || state.identifyToken;
      const email = computeNextVaultData(state.email, action.email);
      const phoneNumber = computeNextVaultData(state.phoneNumber, action.phoneNumber);
      const requirement = action.requirement;

      let requirementHistory = [...state.requirementHistory];
      let disableReset = state.disableReset;
      const currentRequirementKind = state.requirement?.kind;
      if (currentRequirementKind === 'challenge' || currentRequirementKind === 'login') {
        // Don't support going back to a challenge screen since a challenge cannot be undone after the user has logged in.
        // NOTE: the login challenge may be "skipped" if the user decides to make a new account.
        disableReset = true;
        requirementHistory = [];
      } else if (state.requirement) {
        requirementHistory = [state.requirement, ...requirementHistory];
      }

      if (requirementKey(state.requirement) === requirementKey(requirement)) {
        logError(`User is stuck on an identify requirement: ${requirementKey(requirement)}`);
      }

      return {
        identifyToken,
        requirement,
        requirementHistory,
        email,
        phoneNumber,
        disableReset,
        isPasskeyRegistered: wasPasskeyRegistered(state),
        verifiedPrefillContext: action.verifiedPrefillContext,
        displayTermsAndConditions: action.displayTermsAndConditions,
      };
    }
    case 'prev': {
      const { requirementHistory, requirement: _, ...restOfState } = state;
      const [prevRequirement, ...restOfLastHandledRequirements] = requirementHistory;
      return { ...restOfState, requirementHistory: restOfLastHandledRequirements, requirement: prevRequirement };
    }
    case 'resetToLoginWithDifferentAccount': {
      return getInitialState(undefined);
    }
    default:
      return state;
  }
};
